import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_END_POINT } from '../Utils/Constant';
import {
    FiHome,
    FiPlusSquare,
    FiEdit2,
    FiLogOut,
    FiMenu,
    FiX
} from 'react-icons/fi';

// Import the new components
import BlogDashboard from './BlogDashboard';
import BlogCreate from './BlogCreate';
import BlogEdit from './BlogEdit';

const BlogAdmin = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [blogs, setBlogs] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchBlogs();
    }, []);

    useEffect(() => {
        // Filter blogs based on search query
        const filtered = blogs.filter(blog =>
            blog.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredBlogs(filtered);
    }, [searchQuery, blogs]);

    const fetchBlogs = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_END_POINT}astroblog/`);
            setBlogs(response.data);
        } catch (err) {
            setError('Failed to fetch blogs');
            console.error('Error fetching blogs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchBlogs();
    };

    const handleDelete = async (blogId) => {
        if (window.confirm('Are you sure you want to delete this blog?')) {
            try {
                await axios.delete(`${API_END_POINT}astroblog/${blogId}`);
                fetchBlogs();
            } catch (err) {
                setError('Failed to delete blog');
            }
        }
    };

    const handleEdit = (blog) => {
        setEditingBlog(blog);
        setActiveTab('edit');
    };

    const handleLogout = () => {
        // Clear authentication data from localStorage
        localStorage.removeItem('blogToken');
        localStorage.removeItem('blogUser');
        
        // Navigate to login page using window.location for a more direct approach
        window.location.href = '/blog/admin-login';
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setEditingBlog(null); // Reset editing blog when changing tabs
        setIsSidebarOpen(false); // Close sidebar on mobile when changing tabs
    };

    // Render content based on active tab using the new components
    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <BlogDashboard 
                        blogs={blogs}
                        filteredBlogs={filteredBlogs}
                        loading={loading}
                        error={error}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleRefresh={handleRefresh}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                    />
                );
            case 'add':
                return (
                    <BlogCreate 
                        handleTabChange={handleTabChange} 
                    />
                );
            case 'edit':
                return (
                    <BlogEdit 
                        blogs={blogs}
                        editingBlog={editingBlog}
                        setEditingBlog={setEditingBlog}
                        handleEdit={handleEdit}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-r from-white to-success/40">
            <h1 className="text-center text-xs bg-secondary text-gray-200 py-1 md:mb-5">
                Welcome to the AstroTellers
            </h1>
            <div className="w-full md:w-[80%] mx-auto">

                <div className="flex flex-col md:flex-row">
                    {/* Sidebar */}
                    <div className={`fixed md:relative inset-y-0 left-0 z-50 md:z-0 w-64 bg-slate-100 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                        }`}>
                        <div className="flex items-center justify-center p-4 border-b">
                            <h1 className="text-xl font-bold text-center text-secondary">Admin Dashboard</h1>
                            <button
                                onClick={() => setIsSidebarOpen(false)}
                                className="md:hidden text-gray-600 hover:text-gray-800"
                            >
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        <nav className="p-4">
                            <ul className="space-y-2">
                                <li>
                                    <button
                                        onClick={() => handleTabChange('dashboard')}
                                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'dashboard'
                                                ? 'bg-secondary text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        <FiHome className="w-5 h-5" />
                                        Dashboard
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => handleTabChange('add')}
                                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'add'
                                                ? 'bg-secondary text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        <FiPlusSquare className="w-5 h-5" />
                                        Add Blog
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => handleTabChange('edit')}
                                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'edit'
                                                ? 'bg-secondary text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        <FiEdit2 className="w-5 h-5" />
                                        Edit Blog
                                    </button>
                                </li>
                                <li className="mt-8">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleLogout();
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <FiLogOut className="w-5 h-5" />
                                        Logout
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    {/* Main Content */}
                    <div className="md:w-3/4">
                        {/* Mobile Header */}
                        <div className="md:hidden bg-slate-100 shadow-sm p-2">
                            <button
                                onClick={() => setIsSidebarOpen(true)}
                                className="text-gray-600 hover:text-gray-800"
                            >
                                <FiMenu className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content Area */}
                        <main className="min-h-screen bg-slate-100">
                            <div className='flex justify-center p-5 items-center'>
                                <h1 className='text-3xl font-bold font-serif text-secondary'>AstroTellers Blogs Panel</h1>
                            </div>
                            {renderContent()}
                        </main>
                    </div>
                </div>
            </div>
            {/* Footer */}
            <footer className="bg-secondary text-white py-2 mt-5">
                <div className="container mx-auto px-4">
                    <p className="text-center">
                        &copy; {new Date().getFullYear()} AstroTellers blogs. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default BlogAdmin;